/**
 * Authentication Module
 * 
 * Handles all authentication operations including login, registration,
 * SSO, password management, and two-factor authentication.
 * 
 * @package @appfactory/checkpoint-client
 * @version 1.0.0
 */

import { HttpClient } from './http-client';
import {
  AuthResponse,
  SsoAuthResponse,
  RegistrationResponse,
  TwoFactorSetupResponse,
  TwoFactorEnableResponse,
  TwoFactorStatus,
  LoginOptions,
  RegistrationOptions,
  User,
  TokenInfo,
  CheckpointError,
  CheckpointConfig,
} from './types';

/**
 * Authentication module for Checkpoint
 * 
 * @example
 * ```typescript
 * const auth = new CheckpointAuth(httpClient, config);
 * 
 * // Register a new user
 * await auth.register('user@example.com', 'password123', 'username');
 * 
 * // Login
 * const { user, access_token } = await auth.login('user@example.com', 'password123');
 * 
 * // Check if authenticated
 * const isLoggedIn = await auth.isAuthenticated();
 * 
 * // Logout
 * await auth.logout();
 * ```
 */
export class CheckpointAuth {
  private http: HttpClient;
  private config: CheckpointConfig;
  private currentUser: User | null = null;
  private currentMember: Record<string, unknown> | null = null;

  constructor(http: HttpClient, config: CheckpointConfig) {
    this.http = http;
    this.config = config;
  }

  /**
   * Register a new user
   * 
   * @param email User email address
   * @param password User password
   * @param username Optional username
   * @param options Registration options
   * @returns Registration result
   */
  async register(
    email: string,
    password: string,
    username?: string,
    options?: RegistrationOptions
  ): Promise<RegistrationResponse> {
    return this.http.post<RegistrationResponse>('/auth/register', {
      email,
      password,
      username,
      require_verification: options?.requireVerification ?? true,
    });
  }

  /**
   * Login with email and password
   * 
   * @param email User email address
   * @param password User password
   * @param options Login options
   * @returns Authentication response with tokens
   */
  async login(
    email: string,
    password: string,
    options?: LoginOptions
  ): Promise<AuthResponse> {
    const response = await this.http.post<AuthResponse>('/auth/login', {
      email,
      password,
      audience: options?.audience,
      remember_duration: options?.rememberDuration,
    });

    // Check if 2FA is required
    if (response.requires_2fa) {
      return response;
    }

    // Store tokens and user
    await this.http.setTokens(response);
    this.setAuthSession(response);

    return response;
  }

  /**
   * Login with username and password
   * 
   * @param username Username
   * @param password Password
   * @param options Login options
   * @returns Authentication response
   */
  async loginWithUsername(
    username: string,
    password: string,
    options?: LoginOptions
  ): Promise<AuthResponse> {
    const response = await this.http.post<AuthResponse>('/auth/login/username', {
      username,
      password,
      audience: options?.audience,
      remember_duration: options?.rememberDuration,
    });

    if (!response.requires_2fa) {
      await this.http.setTokens(response);
      this.setAuthSession(response);
    }

    return response;
  }

  /**
   * Complete two-factor authentication
   * 
   * @param twoFactorToken Token from login response
   * @param code 2FA code from authenticator or recovery code
   * @param audience Target audience
   * @returns Authentication response
   */
  async completeTwoFactor(
    twoFactorToken: string,
    code: string,
    audience?: string
  ): Promise<AuthResponse> {
    const response = await this.http.post<AuthResponse>('/auth/2fa/complete', {
      two_factor_token: twoFactorToken,
      code,
      audience,
    });

    await this.http.setTokens(response);
    this.setAuthSession(response);

    return response;
  }

  /**
   * SSO Login - Login for a specific target domain
   * 
   * @param identifier User email or username
   * @param password User password
   * @param targetDomain Target domain for SSO
   * @param redirectUrl URL to redirect after authentication
   * @returns SSO authentication response
   */
  async ssoLogin(
    identifier: string,
    password: string,
    targetDomain: string,
    redirectUrl?: string
  ): Promise<SsoAuthResponse> {
    const response = await this.http.post<SsoAuthResponse>('/auth/sso/login', {
      identifier,
      password,
      target_domain: targetDomain,
      redirect_url: redirectUrl,
    });

    if (!response.requires_2fa) {
      await this.http.setTokens(response);
      this.setAuthSession(response);
    }

    return response;
  }

  /**
   * Exchange an SSO ticket for domain-specific tokens
   * 
   * @param ssoTicket SSO ticket from primary domain
   * @param domain Current domain
   * @returns Authentication response
   */
  async ssoExchangeToken(ssoTicket: string, domain: string): Promise<AuthResponse> {
    const response = await this.http.post<AuthResponse>('/auth/sso/exchange', {
      sso_ticket: ssoTicket,
      domain,
    });

    await this.http.setTokens(response);
    this.setAuthSession(response);

    return response;
  }

  /**
   * Redirect to SSO login page
   * 
   * @param returnUrl URL to return to after authentication
   */
  redirectToSsoLogin(returnUrl?: string): void {
    const ssoDomain = this.config.ssoDomain;
    const currentDomain = typeof window !== 'undefined' ? window.location.hostname : '';
    const returnTo = returnUrl ?? (typeof window !== 'undefined' ? window.location.href : '');

    const loginUrl = `${ssoDomain}/login?` + new URLSearchParams({
      target_domain: currentDomain,
      redirect_url: returnTo,
    }).toString();

    if (typeof window !== 'undefined') {
      window.location.href = loginUrl;
    }
  }

  /**
   * Refresh the access token
   * 
   * @returns New token pair
   */
  async refreshToken(): Promise<AuthResponse> {
    const tokens = await this.http.refreshTokens();
    return tokens as AuthResponse;
  }

  /**
   * Verify an access token
   * 
   * @param token Token to verify (uses stored token if not provided)
   * @returns Token information
   */
  async verifyToken(token?: string): Promise<TokenInfo> {
    const tokenToVerify = token ?? (await this.http.getAccessToken());
    
    if (!tokenToVerify) {
      throw new CheckpointError('No token to verify', 'NO_TOKEN', 401);
    }

    return this.http.post<TokenInfo>('/auth/token/verify', { token: tokenToVerify });
  }

  /**
   * Logout the current user
   */
  async logout(): Promise<void> {
    console.log("Logging out user:");
    try {
      const response = await this.http.post('/auth/logout');
      console.log('Logout response:', response);
    } finally {
      await this.http.clearTokens();
      this.currentUser = null;
      this.currentMember = null;
      this.notifyAuthChange(false);
    }
  }
 
  /**
   * Logout from all devices
   */
  async logoutEverywhere(): Promise<void> {
    await this.http.post('/auth/logout/all', undefined, true);
    await this.http.clearTokens();
    this.currentUser = null;
    this.currentMember = null;
    this.notifyAuthChange(false);
  }

  /**
   * Check if the user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    return this.http.isAuthenticated();
  }

  /**
   * Get the current user
   */
  async getCurrentUser(): Promise<User | null> {
    if (this.currentUser) {
      return this.currentUser;
    }

    try {
      const tokenInfo = await this.verifyToken();
      this.currentUser = tokenInfo.user;
      this.currentMember = tokenInfo.member ?? null;
      return this.currentUser;
    } catch {
      return null;
    }
  }

  /**
   * Get the current member record (brain_memberships).
   */
  async getCurrentMember(): Promise<Record<string, unknown> | null> {
    if (this.currentMember) {
      return this.currentMember;
    }

    try {
      const tokenInfo = await this.verifyToken();
      this.currentMember = tokenInfo.member ?? null;
      return this.currentMember;
    } catch {
      return null;
    }
  }

  /**
   * Verify email address
   * 
   * @param selector Verification selector
   * @param token Verification token
   */
  async verifyEmail(selector: string, token: string): Promise<{ message: string }> {
    return this.http.post('/auth/verify-email', { selector, token });
  }

  /**
   * Request password reset email
   * 
   * @param email User email address
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.http.post('/auth/forgot-password', { email });
  }

  /**
   * Reset password with token
   * 
   * @param selector Reset selector
   * @param token Reset token
   * @param password New password
   */
  async resetPassword(
    selector: string,
    token: string,
    password: string
  ): Promise<{ message: string }> {
    return this.http.post('/auth/reset-password', { selector, token, password });
  }

  /**
   * Change password (requires authentication)
   * 
   * @param currentPassword Current password
   * @param newPassword New password
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ message: string }> {
    return this.http.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }, true);
  }

  /**
   * Change email address (requires authentication)
   * 
   * @param newEmail New email address
   */
  async changeEmail(newEmail: string): Promise<{ message: string }> {
    return this.http.post('/auth/change-email', { new_email: newEmail }, true);
  }

  // =====================
  // Two-Factor Authentication
  // =====================

  /**
   * Setup two-factor authentication
   * 
   * @returns Setup data including QR code URI
   */
  async setupTwoFactor(): Promise<TwoFactorSetupResponse> {
    return this.http.post('/auth/2fa/setup', undefined, true);
  }

  /**
   * Enable two-factor authentication
   * 
   * @param secret TOTP secret from setup
   * @param code Verification code from authenticator
   * @returns Enable result with recovery codes
   */
  async enableTwoFactor(secret: string, code: string): Promise<TwoFactorEnableResponse> {
    return this.http.post('/auth/2fa/enable', { secret, code }, true);
  }

  /**
   * Disable two-factor authentication
   * 
   * @param code Current TOTP code
   */
  async disableTwoFactor(code: string): Promise<{ disabled: boolean; message: string }> {
    return this.http.post('/auth/2fa/disable', { code }, true);
  }

  /**
   * Get two-factor authentication status
   */
  async getTwoFactorStatus(): Promise<TwoFactorStatus> {
    return this.http.get('/auth/2fa/status', true);
  }

  /**
   * Generate new recovery codes
   * 
   * @returns New recovery codes
   */
  async generateRecoveryCodes(): Promise<{ recovery_codes: string[] }> {
    return this.http.post('/auth/2fa/recovery-codes', undefined, true);
  }

  /**
   * Notify auth state change
   */
  private setAuthSession(response: AuthResponse): void {
    this.currentUser = response.user;
    this.currentMember = response.member ?? null;
    this.notifyAuthChange(true, response.user);
  }

  private notifyAuthChange(authenticated: boolean, user?: User): void {
    if (this.config.onAuthStateChange) {
      this.config.onAuthStateChange(authenticated, user);
    }
  }
}
